'use client';
import { useTranslations } from 'next-intl';
import type { PropsWithChildren } from 'react';
import { VideoPlayer } from '@/app/components/shared/VideoPlayer';
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogOriginalContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { cn } from '@/app/utils';
import type { CreationItem } from '../types';
import { CreatorAvatar } from './CreatorAvatar';

interface CppCreationDetailProps {
  creation?: CreationItem;
  open: boolean;
  onClose: () => void;
}

export const CppCreationDetail: React.FC<PropsWithChildren<CppCreationDetailProps>> = ({
  creation,
  open,
  onClose,
  children,
}) => {
  const t = useTranslations();

  const renderInfo = () => {
    if (!creation) return null;
    return (
      <div className={cn('flex h-full w-[328px] flex-col overflow-y-auto pr-4 md:w-full lg:w-[268px]')}>
        <div className="flex flex-1 flex-col rounded-8 bg-system-bg03 pt-2 pb-3 md:bg-transparent md:pt-0 md:pb-0">
          <div className="text-sm text-white">
            <div className="border-[rgba(184,221,255,0.08)] border-b px-3 py-2 font-semibold text-white md:hidden">
              {t('cpp.detail.title')}
            </div>
            <div className="flex flex-col gap-3 p-3">
              <div className="flex items-center gap-2">
                <CreatorAvatar
                  nickname={creation.creator?.nick_name ?? ''}
                  avatar={creation.creator?.avatar ?? ''}
                  className="size-6"
                />
                <span>{creation?.creator?.nick_name}</span>
              </div>
              {creation?.title && <div className="font-medium">《{creation?.title}》</div>}
              <div className="text-white/70">{creation?.detail}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVideoPart = () => {
    if (!creation) return null;
    return (
      <div className="relative flex flex-1 flex-col md:min-h-[240px] md:gap-3 md:pr-4">
        <div className="w-full flex-1 rounded-8">
          <VideoPlayer
            src={creation.creation?.uri ?? ''}
            key={`creation_detail_${creation.id}`}
            poster={creation.creation?.cover_uri ?? ''}
            creationId={creation.id ?? ''}
            autoPlay={true}
            hasAudio={true}
            defaultVolume={0.3}
            enableSpaceShortcut
          />
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className="backdrop-blur-2xl" />
        <DialogOriginalContent className="data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[calc(50%-36px)] z-50 mx-auto w-[1308px] translate-x-[-50%] translate-y-[-50%] rounded-12 border border-system-white12 bg-system-bg04 p-4 pr-0 duration-200 md:top-0 md:left-0 md:h-screen md:w-full md:translate-x-0 md:translate-y-0 md:rounded-none md:border-none lg:w-[903px]">
          <DialogTitle className="hidden items-center md:mb-5 md:flex">{t('cpp.detail.title')}</DialogTitle>
          <DialogDescription className="hidden" />
          <DialogClose className="top-4 right-4 hidden md:block" />

          <div className="relative flex h-[559px] max-h-[calc(88vh-32px)] gap-3 text-sm md:h-full md:max-h-[calc(100vh-80px)] md:flex-col md:overflow-y-auto md:pb-[var(--ios-safe-bottom)] lg:text-xs">
            {renderVideoPart()}
            <div className="relative">{renderInfo()}</div>
          </div>
          <div className="-right-14 absolute top-0 h-9 w-9 rounded-8 border border-system-white12 bg-system-white08 text-system-white hover:bg-system-hover01 md:hidden">
            <DialogClose className="h-full w-full text-base" />
          </div>
          <div className="-translate-y-1/2 absolute top-1/2 right-0 translate-x-14 md:hidden lg:translate-x-12">
            {children}
          </div>
        </DialogOriginalContent>
      </DialogPortal>
    </Dialog>
  );
};
